import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ExternalLink, ChevronLeft, ChevronRight, CheckCircle, BookOpen, BarChart2, Layers, Link, HelpCircle, Search, MessageSquare, Menu, X, ChevronDown, Copy, RotateCcw, Milestone } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ChatPanel from '../components/ChatPanel';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TABS = [
  { key: 'keyPoints', icon: BookOpen },
  { key: 'stats', icon: BarChart2 },
  { key: 'schema', icon: Layers },
  { key: 'resources', icon: Link },
  { key: 'quiz', icon: HelpCircle },
];

const COLOR_MAP = {
  coral: { bg: 'bg-[#ff6b6b]/10', text: 'text-[#ff6b6b]', border: 'border-[#ff6b6b]/30' },
  teal: { bg: 'bg-[#20c997]/10', text: 'text-[#20c997]', border: 'border-[#20c997]/30' },
  amber: { bg: 'bg-[#ffc107]/10', text: 'text-[#ffc107]', border: 'border-[#ffc107]/30' },
};

function KeyPoints({ points }) {
  return (
    <div className="space-y-3">
      {(points || []).map((point, i) => (
        <div key={i} className="flex items-start gap-3 bg-[#1a1a24] rounded-xl p-4 border border-white/5 hover:border-[#ff6b6b]/20 transition-colors">
          <span className="w-6 h-6 rounded-full bg-[#ff6b6b]/20 text-[#ff6b6b] text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
          <p className="text-white text-sm leading-relaxed">{point}</p>
        </div>
      ))}
    </div>
  );
}

function Stats({ stats }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {(stats || []).map((stat, i) => {
        const colors = [
          { bg: 'bg-[#ff6b6b]/10', text: 'text-[#ff6b6b]' },
          { bg: 'bg-[#20c997]/10', text: 'text-[#20c997]' },
          { bg: 'bg-[#ffc107]/10', text: 'text-[#ffc107]' },
        ];
        const c = colors[i % colors.length];
        return (
          <div key={i} className={`${c.bg} rounded-2xl p-5 text-center border border-white/5`}>
            <div className={`font-heading text-3xl font-bold ${c.text} mb-1`}>
              {stat.value}<span className="text-lg">{stat.unit}</span>
            </div>
            <p className="text-[#e8e8e8] text-xs leading-relaxed">{stat.label}</p>
          </div>
        );
      })}
    </div>
  );
}

function Schema({ schema }) {
  const { t } = useTranslation();
  if (!schema) return <p className="text-[#e8e8e8] text-sm">{t('course.noSchema')}</p>;
  return (
    <div>
      <h3 className="font-heading font-semibold text-white text-lg mb-2">{schema.title}</h3>
      <p className="text-[#e8e8e8] text-sm mb-6 leading-relaxed">{schema.description}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(schema.elements || []).map((el, i) => {
          const c = COLOR_MAP[el.color] || COLOR_MAP.coral;
          return (
            <div key={i} className={`rounded-2xl p-4 border ${c.bg} ${c.border}`}>
              <div className={`font-semibold text-sm ${c.text} mb-1`}>{el.label}</div>
              <p className="text-[#e8e8e8] text-xs leading-relaxed">{el.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Resources({ resources }) {
  const { t } = useTranslation();

  const TYPE_LABELS = {
    article: t('course.typeArticle'),
    video: t('course.typeVideo'),
    course: t('course.typeCourse'),
    officiel: t('course.typeOfficiel'),
    guide: t('course.typeGuide'),
    loi: t('course.typeLoi'),
    etude: t('course.typeEtude'),
  };

  const TYPE_COLORS = {
    article: 'bg-[#20c997]/10 text-[#20c997]',
    video: 'bg-[#ff6b6b]/10 text-[#ff6b6b]',
    course: 'bg-[#20c997]/10 text-[#20c997]',
    officiel: 'bg-[#4d9eff]/10 text-[#4d9eff]',
    guide: 'bg-[#ffc107]/10 text-[#ffc107]',
    loi: 'bg-[#ff6b6b]/10 text-[#ff6b6b]',
    etude: 'bg-[#a78bfa]/10 text-[#a78bfa]',
  };

  return (
    <div className="space-y-3">
      {(resources || []).map((res, i) => {
        const isFallback = !!res.is_fallback;
        const href = isFallback ? (res.search_url || '#') : (res.url || '#');
        const typeLabel = TYPE_LABELS[res.type] || res.type || '';
        const typeColor = TYPE_COLORS[res.type] || 'bg-white/5 text-[#e8e8e8]';

        return (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-start gap-3 rounded-xl p-4 border transition-colors group ${
              isFallback
                ? 'bg-[#1a1a24] border-[#ffc107]/10 hover:border-[#ffc107]/30'
                : 'bg-[#1a1a24] border-white/5 hover:border-[#20c997]/30'
            }`}
            data-testid={`resource-link-${i}`}
          >
            {/* Icon */}
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
              isFallback ? 'bg-[#ffc107]/10' : 'bg-[#20c997]/10'
            }`}>
              {isFallback
                ? <Search size={15} className="text-[#ffc107]" />
                : <ExternalLink size={15} className="text-[#20c997]" />
              }
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              {/* Badges */}
              {(res.source_name || typeLabel) && (
                <div className="flex items-center flex-wrap gap-1.5 mb-1.5">
                  {res.source_name && (
                    <span className="text-[10px] font-medium text-[#a0a0ab] bg-white/5 px-2 py-0.5 rounded-full border border-white/5">
                      {res.source_name}
                    </span>
                  )}
                  {typeLabel && (
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${typeColor}`}>
                      {typeLabel}
                    </span>
                  )}
                  {isFallback && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#ffc107]/10 text-[#ffc107]">
                      {t('course.resourceFallbackBadge')}
                    </span>
                  )}
                </div>
              )}

              {/* Title */}
              <p className="text-white text-sm font-medium leading-snug">
                {isFallback
                  ? t('course.resourceFallbackLabel', { source: res.source_name || 'source' })
                  : res.title
                }
              </p>

              {/* Relevance reason */}
              {res.relevance_reason && !isFallback && (
                <p className="text-[#a0a0ab] text-xs mt-1.5 leading-relaxed">{res.relevance_reason}</p>
              )}
            </div>

            {/* Arrow */}
            <ExternalLink size={14} className={`shrink-0 mt-1 transition-colors ${
              isFallback
                ? 'text-[#ffc107]/40 group-hover:text-[#ffc107]'
                : 'text-[#e8e8e8]/40 group-hover:text-[#20c997]'
            }`} />
          </a>
        );
      })}
    </div>
  );
}

function Quiz({ quiz, lessonId, courseId, onComplete }) {
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const answerArray = (quiz || []).map((_, i) => answers[i] ?? -1);
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/courses/${courseId}/quiz`,
        { lesson_id: lessonId, answers: answerArray },
        { withCredentials: true }
      );
      setResults(data);
      await refreshUser();
      if (onComplete) onComplete(data);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (results) {
    return (
      <div className="space-y-4">
        <div className="kurso-card p-5 text-center">
          <div className={`font-heading text-4xl font-bold mb-2 ${results.score >= 0.7 ? 'text-[#20c997]' : 'text-[#ff6b6b]'}`}>
            {results.correct}/{results.total}
          </div>
          <p className="text-[#e8e8e8] text-sm">{t('course.scorePercent', { pct: Math.round(results.score * 100) })}</p>
          {results.xp_gained > 0 && (
            <div className="xp-badge mt-3 inline-flex items-center gap-1">
              +{results.xp_gained} XP
            </div>
          )}
        </div>
        {results.results.map((r, i) => (
          <div key={i} className={`rounded-xl p-4 border ${r.is_correct ? 'border-[#20c997]/30 bg-[#20c997]/5' : 'border-[#ff6b6b]/30 bg-[#ff6b6b]/5'}`}>
            <p className="text-white text-sm font-medium mb-2">{r.question}</p>
            <p className={`text-sm font-medium ${r.is_correct ? 'text-[#20c997]' : 'text-[#ff6b6b]'}`}>
              {r.is_correct ? t('quiz.correct') : t('quiz.incorrect')} → {quiz[i]?.options[r.correct_index]}
            </p>
            <p className="text-[#e8e8e8] text-xs mt-1.5 leading-relaxed">{r.explanation}</p>
          </div>
        ))}
        {results.new_badges?.length > 0 && (
          <div className="teal-badge px-4 py-3 rounded-2xl text-center">
            {t('course.newBadgeLine', { badges: results.new_badges.join(', ') })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {(quiz || []).map((q, i) => (
        <div key={i} className="kurso-card p-4">
          <p className="text-white font-medium text-sm mb-3">
            <span className="text-[#e8e8e8] text-xs">{i + 1}. </span>{q.question}
          </p>
          <div className="space-y-2">
            {q.options.map((opt, j) => (
              <button
                key={j}
                onClick={() => setAnswers(prev => ({ ...prev, [i]: j }))}
                className={`w-full text-left text-sm px-4 py-3 rounded-xl border transition-all ${
                  answers[i] === j
                    ? 'border-[#ff6b6b] bg-[#ff6b6b]/10 text-white'
                    : 'border-white/10 text-[#e8e8e8] hover:border-white/20 hover:text-white'
                }`}
                data-testid={`quiz-option-${i}-${j}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={handleSubmit}
        disabled={submitting || Object.keys(answers).length < (quiz?.length || 0)}
        className="btn-primary w-full py-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="quiz-submit-btn"
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-[#0f0e17] border-t-transparent animate-spin" />
            {t('course.correcting')}
          </span>
        ) : t('quiz.submit')}
      </button>
    </div>
  );
}

// ── Markdown simple (sans dépendance externe) ─────────────────────────────────
const PURIFY_INLINE_OPTS = { ALLOWED_TAGS: ['strong', 'em', 'code', 'span', 'br'], ALLOWED_ATTR: ['class'] };

function formatInline(text) {
  const escaped = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em class="text-[#94a3b8]">$1</em>')
    .replace(/`(.*?)`/g, '<code class="bg-[#1a1a24] text-[#20c997] px-1 py-0.5 rounded text-xs font-mono">$1</code>');
  return DOMPurify.sanitize(escaped, PURIFY_INLINE_OPTS);
}

function SimpleMarkdown({ content }) {
  if (!content) return null;
  const lines = (typeof content === 'string' ? content : '').split('\n');
  const elements = [];
  let listBuf = [];

  const flushList = (key) => {
    if (listBuf.length > 0) {
      elements.push(
        <ul key={`ul-${key}`} className="space-y-1.5 mb-2">
          {listBuf.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#e8e8e8]">
              <span className="text-[#ff6b6b] shrink-0 mt-1">▸</span>
              <span dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
            </li>
          ))}
        </ul>
      );
      listBuf = [];
    }
  };

  lines.forEach((line, i) => {
    if (line.startsWith('### ')) {
      flushList(i);
      elements.push(<h3 key={i} className="font-heading text-sm font-semibold text-white mt-3 mb-1.5">{line.slice(4)}</h3>);
    } else if (line.startsWith('## ')) {
      flushList(i);
      elements.push(<h2 key={i} className="font-heading text-base font-bold text-white mt-4 mb-2">{line.slice(3)}</h2>);
    } else if (line.startsWith('# ')) {
      flushList(i);
      elements.push(<h1 key={i} className="font-heading text-lg font-bold text-white mt-5 mb-2">{line.slice(2)}</h1>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      listBuf.push(line.slice(2));
    } else if (line.trim() === '') {
      flushList(i);
      elements.push(<div key={i} className="h-1.5" />);
    } else {
      flushList(i);
      elements.push(
        <p key={i} className="text-sm text-[#e8e8e8] leading-relaxed"
          dangerouslySetInnerHTML={{ __html: formatInline(line) }} />
      );
    }
  });
  flushList('end');
  return <div className="space-y-1">{elements}</div>;
}

// ── Block Readers ─────────────────────────────────────────────────────────────
function TextBlockReader({ block }) {
  const content = typeof block.content === 'string' ? block.content : '';
  return (
    <div className="kurso-card p-5" data-testid="text-block-reader">
      <SimpleMarkdown content={content} />
    </div>
  );
}

function StatsBlockReader({ block }) {
  const items = block.content?.items || [];
  const COLORS = [
    { bg: 'bg-[#ff6b6b]/10', text: 'text-[#ff6b6b]' },
    { bg: 'bg-[#20c997]/10', text: 'text-[#20c997]' },
    { bg: 'bg-[#ffc107]/10', text: 'text-[#ffc107]' },
  ];
  if (items.length === 0) return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="stats-block-reader">
      {items.map((stat, i) => {
        const c = COLORS[i % COLORS.length];
        return (
          <div key={i} className={`${c.bg} rounded-2xl p-5 text-center border border-white/5`}>
            <div className={`font-heading text-3xl font-bold ${c.text} mb-1`}>
              {stat.value}<span className="text-lg">{stat.unit}</span>
            </div>
            <p className="text-[#e8e8e8] text-xs leading-relaxed">{stat.label}</p>
          </div>
        );
      })}
    </div>
  );
}

function SchemaBlockReader({ block }) {
  const content = block.content || {};
  const elements = content.elements || [];
  const CM = {
    coral: 'bg-[#ff6b6b]/10 border-[#ff6b6b]/30 text-[#ff6b6b]',
    teal:  'bg-[#20c997]/10 border-[#20c997]/30 text-[#20c997]',
    amber: 'bg-[#ffc107]/10 border-[#ffc107]/30 text-[#ffc107]',
  };
  return (
    <div data-testid="schema-block-reader">
      {content.title && <h3 className="font-heading font-semibold text-white text-base mb-2">{content.title}</h3>}
      {content.description && <p className="text-[#94a3b8] text-sm mb-4 leading-relaxed">{content.description}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {elements.map((el, i) => {
          const cls = CM[el.color] || CM.coral;
          return (
            <div key={i} className={`rounded-2xl p-4 border ${cls}`}>
              <div className="font-semibold text-sm mb-1">{el.label}</div>
              <p className="text-[#e8e8e8] text-xs leading-relaxed opacity-90">{el.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VideoBlockReader({ block }) {
  const url = typeof block.content === 'string' ? block.content : (block.content?.url || '');
  const title = block.content?.title || '';

  const getYouTubeId = (u) => {
    const m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : null;
  };
  const ytId = getYouTubeId(url);

  return (
    <div data-testid="video-block-reader">
      {title && <p className="text-white font-medium text-sm mb-3">{title}</p>}
      {ytId ? (
        <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${ytId}`}
            title={title || 'Video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-[#20c997] text-sm hover:underline"
        >
          <ExternalLink size={14} /> {url}
        </a>
      ) : (
        <p className="text-[#64748b] text-sm">Aucune vidéo</p>
      )}
    </div>
  );
}

function CodeBlockReader({ block }) {
  const [copied, setCopied] = useState(false);
  const code = block.content?.code || (typeof block.content === 'string' ? block.content : '');
  const language = block.content?.language || 'code';

  const handleCopy = () => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-[#0d0d14] border border-[#2a2935] rounded-2xl overflow-hidden" data-testid="code-block-reader">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#2a2935] bg-[#0f0e17]">
        <span className="text-xs text-[#64748b] font-mono font-semibold">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-xs text-[#64748b] hover:text-white transition-colors"
        >
          {copied ? <><CheckCircle size={11} className="text-[#20c997]" /> Copié</> : <><Copy size={11} /> Copier</>}
        </button>
      </div>
      <pre className="px-5 py-4 text-sm text-[#e8e8e8] font-mono overflow-x-auto leading-relaxed whitespace-pre">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function FlashcardBlockReader({ block }) {
  // Handles both single-card {front, back} and multi-card {cards: [{front, back}]}
  const cards = block.content?.cards?.length
    ? block.content.cards
    : (block.content?.front ? [{ front: block.content.front, back: block.content.back }] : []);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);

  if (cards.length === 0) return null;
  const card = cards[currentIdx];

  return (
    <div className="space-y-3" data-testid="flashcard-block-reader">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold text-[#4d9eff] uppercase tracking-wider">Flashcards</p>
        <p className="text-xs text-[#64748b]">{currentIdx + 1} / {cards.length}</p>
      </div>
      <div onClick={() => setFlipped(p => !p)} className="cursor-pointer">
        <div className={`border rounded-2xl p-8 text-center min-h-[160px] flex flex-col items-center justify-center transition-all duration-300 ${
          flipped ? 'bg-[#20c997]/5 border-[#20c997]/30' : 'bg-[#1a1a24] border-[#4d9eff]/20 hover:border-[#4d9eff]/40'
        }`}>
          <p className={`text-[10px] uppercase tracking-[0.2em] font-bold mb-3 ${flipped ? 'text-[#20c997]' : 'text-[#4d9eff]'}`}>
            {flipped ? 'Réponse' : 'Question'}
          </p>
          <p className={`text-base leading-relaxed font-medium ${flipped ? 'text-[#e8e8e8]' : 'text-white'}`}>
            {flipped ? card.back : card.front}
          </p>
          <p className="text-xs text-[#64748b] mt-5 flex items-center gap-1.5">
            <RotateCcw size={11} /> Cliquez pour retourner
          </p>
        </div>
      </div>
      {cards.length > 1 && (
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => { setCurrentIdx(i => Math.max(0, i - 1)); setFlipped(false); }}
            disabled={currentIdx === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs text-[#94a3b8] border border-[#2a2935] hover:text-white hover:border-[#3a3945] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={13} /> Précédente
          </button>
          <div className="flex gap-1.5">
            {cards.map((_, i) => (
              <button key={i} onClick={() => { setCurrentIdx(i); setFlipped(false); }}
                className={`w-2 h-2 rounded-full transition-all ${i === currentIdx ? 'bg-[#4d9eff]' : 'bg-[#2a2935]'}`}
              />
            ))}
          </div>
          <button
            onClick={() => { setCurrentIdx(i => Math.min(cards.length - 1, i + 1)); setFlipped(false); }}
            disabled={currentIdx === cards.length - 1}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs text-[#94a3b8] border border-[#2a2935] hover:text-white hover:border-[#3a3945] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Suivante <ChevronRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

function TimelineBlockReader({ block }) {
  // Handles both 'steps' (old format) and 'events' (new MODULE_CONTENT_PROMPT format)
  const rawItems = block.content?.steps || block.content?.events || [];
  const steps = rawItems.map(s => ({
    label: s.label || s.date || '',
    title: s.title || '',
    description: s.description || '',
  }));
  if (steps.length === 0) return null;
  return (
    <div className="space-y-0" data-testid="timeline-block-reader">
      {steps.map((step, i) => (
        <div key={i} className="flex gap-4">
          <div className="flex flex-col items-center">
            <div className="w-8 h-8 rounded-full bg-[#f97316]/10 border-2 border-[#f97316]/40 flex items-center justify-center shrink-0">
              <span className="text-[#f97316] text-xs font-bold">{i + 1}</span>
            </div>
            {i < steps.length - 1 && <div className="w-0.5 flex-1 bg-[#2a2935] mt-1.5 mb-1.5 min-h-[24px]" />}
          </div>
          <div className="pb-6 flex-1">
            {step.label && <p className="text-[10px] text-[#f97316] font-bold uppercase tracking-wider mb-1">{step.label}</p>}
            <p className="text-white text-sm font-medium">{step.title}</p>
            {step.description && <p className="text-[#94a3b8] text-xs mt-1.5 leading-relaxed">{step.description}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

function VideoEmbedBlockReader({ block }) {
  const content = block.content || {};
  const url = content.url || '';
  const title = content.title || '';
  const description = content.description || '';

  const getYouTubeId = (u) => {
    const m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : null;
  };
  const ytId = getYouTubeId(url);

  return (
    <div className="space-y-3" data-testid="video-embed-block-reader">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-6 h-6 rounded-lg bg-[#ff6b6b]/10 flex items-center justify-center">
          <ExternalLink size={12} className="text-[#ff6b6b]" />
        </div>
        <p className="text-[10px] font-bold text-[#ff6b6b] uppercase tracking-wider">Vidéo</p>
      </div>
      {title && <p className="text-white font-medium text-sm">{title}</p>}
      {ytId ? (
        <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${ytId}`}
            title={title || 'Vidéo'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : url ? (
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-2 bg-[#1a1a24] border border-[#2a2935] rounded-xl px-4 py-3 text-[#20c997] text-sm hover:border-[#20c997]/30 transition-all">
          <ExternalLink size={14} /> {url}
        </a>
      ) : null}
      {description && <p className="text-[#94a3b8] text-sm leading-relaxed">{description}</p>}
    </div>
  );
}

function OpenQuestionBlockReader({ block }) {
  const content = block.content || {};
  const [answer, setAnswer] = useState('');
  const [showSample, setShowSample] = useState(false);

  return (
    <div className="bg-[#1a1a24] border border-[#ffd93d]/20 rounded-2xl p-5 space-y-4" data-testid="open-question-block-reader">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-lg bg-[#ffd93d]/10 flex items-center justify-center">
          <HelpCircle size={12} className="text-[#ffd93d]" />
        </div>
        <p className="text-[#ffd93d] text-[10px] font-bold uppercase tracking-wider">Question ouverte</p>
      </div>
      <p className="text-white font-medium text-sm leading-relaxed">{content.question}</p>
      {content.instructions && (
        <p className="text-[#94a3b8] text-xs italic">{content.instructions}</p>
      )}
      <textarea
        value={answer}
        onChange={e => setAnswer(e.target.value)}
        placeholder="Rédigez votre réflexion ici..."
        rows={4}
        className="w-full bg-[#0f0e17] border border-[#2a2935] rounded-xl px-4 py-3 text-sm text-white placeholder-[#64748b] resize-none focus:outline-none focus:border-[#ffd93d]/40 transition-colors"
      />
      {content.sample_answer && (
        <div>
          <button
            onClick={() => setShowSample(p => !p)}
            className="flex items-center gap-2 text-xs text-[#64748b] hover:text-white transition-colors"
          >
            <ChevronDown size={13} className={`transition-transform duration-200 ${showSample ? 'rotate-180' : ''}`} />
            {showSample ? 'Masquer' : 'Voir'} la réponse modèle
          </button>
          {showSample && (
            <div className="mt-3 p-4 bg-[#20c997]/5 border border-[#20c997]/20 rounded-xl">
              <p className="text-[#e8e8e8] text-sm leading-relaxed">{content.sample_answer}</p>
              {content.evaluation_criteria?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#20c997]/10">
                  <p className="text-[#20c997] text-[10px] font-bold uppercase tracking-wider mb-2">Critères d'évaluation</p>
                  <ul className="space-y-1">
                    {content.evaluation_criteria.map((c, i) => (
                      <li key={i} className="text-[#94a3b8] text-xs flex items-start gap-2">
                        <span className="text-[#20c997] mt-0.5">•</span> {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DragDropBlockReader({ block }) {
  const content = block.content || {};
  const allItems = content.items || [];
  const categories = content.categories || [];
  const [assignments, setAssignments] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const correct = allItems.filter(item => assignments[item.id] === item.category).length;

  if (allItems.length === 0 || categories.length === 0) return null;

  return (
    <div className="space-y-4" data-testid="drag-drop-block-reader">
      <div className="flex items-start gap-2">
        <div className="w-6 h-6 rounded-lg bg-[#a78bfa]/10 flex items-center justify-center mt-0.5 shrink-0">
          <Layers size={12} className="text-[#a78bfa]" />
        </div>
        <div>
          <p className="text-white font-medium text-sm">{content.title || "Activité d'association"}</p>
          {content.instructions && <p className="text-[#64748b] text-xs mt-0.5">{content.instructions}</p>}
        </div>
      </div>

      {!submitted ? (
        <>
          <div className="space-y-2">
            {allItems.map(item => (
              <div key={item.id} className="bg-[#0f0e17] border border-[#2a2935] rounded-xl p-3">
                <p className="text-white text-sm mb-2">{item.text}</p>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setAssignments(prev => ({ ...prev, [item.id]: cat.id }))}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                        assignments[item.id] === cat.id
                          ? 'bg-[#a78bfa] text-white'
                          : 'bg-[#1a1a24] border border-[#2a2935] text-[#94a3b8] hover:text-white hover:border-[#a78bfa]/40'
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => setSubmitted(true)}
            disabled={Object.keys(assignments).length < allItems.length}
            className="w-full py-3 rounded-xl text-sm font-semibold bg-[#a78bfa] text-white disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
            data-testid="drag-drop-submit-btn"
          >
            Vérifier mes associations
          </button>
        </>
      ) : (
        <div className="space-y-2">
          <div className="text-center py-3">
            <p className={`text-3xl font-bold font-heading ${correct === allItems.length ? 'text-[#20c997]' : 'text-[#ff6b6b]'}`}>
              {correct}/{allItems.length}
            </p>
            <p className="text-[#94a3b8] text-xs mt-1">bonnes associations</p>
          </div>
          {allItems.map(item => {
            const isCorrect = assignments[item.id] === item.category;
            const correctCat = categories.find(c => c.id === item.category);
            return (
              <div key={item.id} className={`rounded-xl p-3 border text-sm ${isCorrect ? 'border-[#20c997]/30 bg-[#20c997]/5' : 'border-[#ff6b6b]/30 bg-[#ff6b6b]/5'}`}>
                <p className="text-white">{item.text}</p>
                <p className={`text-xs mt-1 font-medium ${isCorrect ? 'text-[#20c997]' : 'text-[#ff6b6b]'}`}>
                  {isCorrect ? 'Correct' : `Incorrect → ${correctCat?.label || item.category}`}
                </p>
              </div>
            );
          })}
          <button
            onClick={() => { setSubmitted(false); setAssignments({}); }}
            className="w-full py-2.5 rounded-xl text-xs text-[#94a3b8] border border-[#2a2935] hover:text-white hover:border-[#3a3945] transition-all"
          >
            Réessayer
          </button>
        </div>
      )}
    </div>
  );
}

function QuizBlockReader({ block, courseId, lessonId, onComplete }) {
  const questions = block.content?.questions || [];
  const { refreshUser } = useAuth();
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    const answerArray = questions.map((_, i) => answers[i] ?? -1);
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/courses/${courseId}/quiz`,
        { lesson_id: lessonId, answers: answerArray },
        { withCredentials: true }
      );
      setResults(data);
      await refreshUser();
      if (onComplete) onComplete(data);
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (questions.length === 0) return null;

  if (results) {
    return (
      <div className="space-y-4" data-testid="quiz-block-results">
        <div className="kurso-card p-5 text-center">
          <div className={`font-heading text-4xl font-bold mb-2 ${results.score >= 0.7 ? 'text-[#20c997]' : 'text-[#ff6b6b]'}`}>
            {results.correct}/{results.total}
          </div>
          <p className="text-[#e8e8e8] text-sm">{Math.round(results.score * 100)}% de réussite</p>
          {results.xp_gained > 0 && <div className="xp-badge mt-3 inline-flex items-center gap-1">+{results.xp_gained} XP</div>}
        </div>
        {results.results?.map((r, i) => (
          <div key={i} className={`rounded-xl p-4 border ${r.is_correct ? 'border-[#20c997]/30 bg-[#20c997]/5' : 'border-[#ff6b6b]/30 bg-[#ff6b6b]/5'}`}>
            <p className="text-white text-sm font-medium mb-1">{r.question}</p>
            <p className={`text-sm font-medium ${r.is_correct ? 'text-[#20c997]' : 'text-[#ff6b6b]'}`}>
              {r.is_correct ? 'Correct' : 'Incorrect'} → {questions[i]?.options?.[r.correct_index]}
            </p>
            {r.explanation && <p className="text-[#e8e8e8] text-xs mt-1.5 leading-relaxed">{r.explanation}</p>}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="quiz-block-reader">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-6 h-6 rounded-lg bg-[#ffd93d]/10 flex items-center justify-center">
          <HelpCircle size={13} className="text-[#ffd93d]" />
        </div>
        <p className="text-white font-semibold text-sm">Quiz de vérification</p>
      </div>
      {questions.map((q, i) => (
        <div key={i} className="kurso-card p-4">
          <p className="text-white font-medium text-sm mb-3">
            <span className="text-[#64748b] text-xs">{i + 1}. </span>{q.question}
          </p>
          <div className="space-y-2">
            {(q.options || []).map((opt, j) => (
              <button
                key={j}
                onClick={() => setAnswers(prev => ({ ...prev, [i]: j }))}
                className={`w-full text-left text-sm px-4 py-3 rounded-xl border transition-all ${
                  answers[i] === j
                    ? 'border-[#ff6b6b] bg-[#ff6b6b]/10 text-white'
                    : 'border-white/10 text-[#e8e8e8] hover:border-white/20 hover:text-white'
                }`}
                data-testid={`quiz-block-option-${i}-${j}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={handleSubmit}
        disabled={submitting || Object.keys(answers).length < questions.length}
        className="btn-primary w-full py-4 text-sm disabled:opacity-50"
        data-testid="quiz-block-submit-btn"
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-2">
            <div className="w-4 h-4 rounded-full border-2 border-[#0f0e17] border-t-transparent animate-spin" />
            Correction en cours...
          </span>
        ) : 'Valider mes réponses'}
      </button>
    </div>
  );
}

// ── Blocks Reader (mode blocs) ────────────────────────────────────────────────
// ── NarratedAudioBlock ───────────────────────────────────────────────────────
function NarratedAudioBlock({ block }) {
  const audioRef = React.useRef(null);
  const [words, setWords] = React.useState([]);
  const [currentWordIdx, setCurrentWordIdx] = React.useState(-1);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [progressPct, setProgressPct] = React.useState(0);

  React.useEffect(() => {
    const alignment = block.content?.alignment || {};
    const chars = alignment.characters || [];
    const starts = alignment.character_start_times_seconds || [];
    const ends = alignment.character_end_times_seconds || [];
    const wordList = [];
    let curWord = '';
    let wordStart = 0;
    let wordEnd = 0;
    for (let i = 0; i <= chars.length; i++) {
      const ch = chars[i];
      const isSpace = ch === ' ' || ch === undefined;
      if (isSpace) {
        if (curWord.trim()) wordList.push({ word: curWord, start: wordStart, end: wordEnd });
        curWord = '';
        if (i + 1 < chars.length) wordStart = starts[i + 1] || 0;
      } else {
        if (!curWord) wordStart = starts[i] || 0;
        curWord += ch;
        wordEnd = ends[i] || 0;
      }
    }
    setWords(wordList);
  }, [block.content?.alignment]);

  const handleTimeUpdate = () => {
    const el = audioRef.current;
    if (!el) return;
    const time = el.currentTime || 0;
    setCurrentWordIdx(words.findLastIndex(w => time >= w.start) ?? -1);
    if (el.duration) setProgressPct((time / el.duration) * 100);
  };

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play(); else el.pause();
  };

  const audioSrc = block.content?.audio_base64 ? `data:audio/mpeg;base64,${block.content.audio_base64}` : null;
  const displayText = block.content?.script_adapted || block.content?.script || '';
  const imageUrl = block.content?.image_url || '';
  const subtitleWindow = words.slice(Math.max(0, currentWordIdx - 4), currentWordIdx + 6);

  if (!audioSrc && !displayText) return null;

  return (
    <div className="rounded-2xl overflow-hidden border border-[#20c997]/20 bg-[#0f0e17]" data-testid="narrated-audio-block">
      <div
        className="relative aspect-video w-full bg-gradient-to-br from-[#1a1a28] to-[#0f0e17] cursor-pointer select-none"
        onClick={togglePlay}
        data-testid="ai-video-stage"
      >
        {imageUrl && (
          <img src={imageUrl} alt="" className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ${isPlaying ? 'opacity-60 scale-105' : 'opacity-40'}`} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f0e17] via-[#0f0e17]/30 to-transparent" />

        <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/40 backdrop-blur-md rounded-full px-3 py-1">
          <span className={`w-2 h-2 rounded-full ${isPlaying ? 'bg-[#ff6b6b] animate-pulse' : 'bg-[#20c997]'}`} />
          <span className="text-[10px] font-semibold text-white uppercase tracking-wider">Vidéo d'introduction · voix IA</span>
        </div>

        {block.content?.title && (
          <p className="absolute top-3 right-3 max-w-[45%] text-right text-[11px] text-white/70 truncate">{block.content.title}</p>
        )}

        {!isPlaying && audioSrc && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-[#20c997] flex items-center justify-center shadow-[0_0_40px_rgba(32,201,151,0.45)] hover:scale-105 transition-transform" data-testid="ai-video-play">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="#0f0e17"><polygon points="6 3 20 12 6 21 6 3" /></svg>
            </div>
          </div>
        )}

        <div className="absolute bottom-4 left-4 right-4 text-center">
          {words.length > 0 ? (
            <p className="text-base sm:text-lg font-medium leading-relaxed drop-shadow-lg">
              {subtitleWindow.map((w, i) => (
                <span key={`sub-${currentWordIdx}-${i}`} className={`px-0.5 ${words[currentWordIdx] === w ? 'text-[#20c997] font-bold' : 'text-white'}`}>{w.word} </span>
              ))}
            </p>
          ) : (
            <p className="text-sm text-white/90 line-clamp-2 drop-shadow-lg">{displayText}</p>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
          <div className="h-full bg-[#20c997] transition-all duration-200" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {audioSrc && (
        <audio
          ref={audioRef}
          src={audioSrc}
          controls
          onTimeUpdate={handleTimeUpdate}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => { setIsPlaying(false); setCurrentWordIdx(-1); setProgressPct(0); }}
          className="w-full h-9 accent-[#20c997]"
          style={{ colorScheme: 'dark' }}
          data-testid="ai-video-audio"
        />
      )}

      {displayText && (
        <details className="px-4 py-3 border-t border-white/5">
          <summary className="text-xs text-[#94a3b8] cursor-pointer hover:text-white transition-colors">Transcription</summary>
          <p className="text-sm leading-7 mt-2">
            {words.length > 0 ? words.map((w, i) => (
              <span key={`word-${i}`} className={`rounded px-0.5 ${i === currentWordIdx ? 'text-white bg-[#20c997]/20 font-medium' : i < currentWordIdx ? 'text-[#64748b]' : 'text-[#94a3b8]'}`}>{w.word}{' '}</span>
            )) : <span className="text-[#94a3b8]">{displayText}</span>}
          </p>
        </details>
      )}
    </div>
  );
}


const BLOCKS_PER_PAGE = 3;

function BlocksReader({ blocks, courseId, moduleId, lesson, resources, onComplete }) {
  const [currentPage, setCurrentPage] = useState(0);
  const readerRef = React.useRef(null);

  const totalPages = Math.ceil(blocks.length / BLOCKS_PER_PAGE);
  const isLastPage = currentPage >= totalPages - 1;
  const pageBlocks = blocks.slice(currentPage * BLOCKS_PER_PAGE, (currentPage + 1) * BLOCKS_PER_PAGE);

  const handleNext = () => {
    setCurrentPage(p => p + 1);
    readerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderBlock = (block, globalIdx) => {
    switch (block.type) {
      case 'narrated_audio': return <NarratedAudioBlock   key={block.id || globalIdx} block={block} />;
      case 'text':           return <TextBlockReader      key={block.id || globalIdx} block={block} />;
      case 'stats':          return <StatsBlockReader     key={block.id || globalIdx} block={block} />;
      case 'schema':         return <SchemaBlockReader    key={block.id || globalIdx} block={block} />;
      case 'video':          return <VideoBlockReader     key={block.id || globalIdx} block={block} />;
      case 'video_embed':    return <VideoEmbedBlockReader key={block.id || globalIdx} block={block} />;
      case 'code':           return <CodeBlockReader      key={block.id || globalIdx} block={block} />;
      case 'flashcard':      return <FlashcardBlockReader key={block.id || globalIdx} block={block} />;
      case 'flashcards':     return <FlashcardBlockReader key={block.id || globalIdx} block={block} />;
      case 'timeline':       return <TimelineBlockReader  key={block.id || globalIdx} block={block} />;
      case 'open_question':  return <OpenQuestionBlockReader key={block.id || globalIdx} block={block} />;
      case 'drag_drop':      return <DragDropBlockReader  key={block.id || globalIdx} block={block} />;
      case 'quiz':           return <QuizBlockReader      key={block.id || globalIdx} block={block} courseId={courseId} lessonId={lesson?.id || moduleId} onComplete={onComplete} />;
      default:               return null;
    }
  };

  return (
    <div className="space-y-5" data-testid="blocks-reader" ref={readerRef}>
      {/* Barre de progression pagination */}
      {totalPages > 1 && (
        <div className="flex items-center gap-3" data-testid="blocks-progress">
          <div className="flex-1 h-1 bg-[#1a1a24] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#ff6b6b] to-[#20c997] rounded-full transition-all duration-500"
              style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }}
            />
          </div>
          <span className="text-xs text-[#64748b] shrink-0">
            {currentPage * BLOCKS_PER_PAGE + 1}–{Math.min((currentPage + 1) * BLOCKS_PER_PAGE, blocks.length)}&nbsp;/&nbsp;{blocks.length} blocs
          </span>
        </div>
      )}

      {/* Blocs de la page courante */}
      {pageBlocks.map((block, i) => renderBlock(block, currentPage * BLOCKS_PER_PAGE + i))}

      {/* Ressources : uniquement sur la dernière page */}
      {isLastPage && resources && resources.length > 0 && (
        <div>
          <p className="text-xs text-[#64748b] uppercase tracking-widest font-bold mb-3">Ressources complémentaires</p>
          <Resources resources={resources} />
        </div>
      )}

      {/* Bouton Continuer / pas sur la dernière page */}
      {!isLastPage && (
        <button
          onClick={handleNext}
          className="w-full flex items-center justify-center gap-2 bg-[#16161e] border border-[#2a2935] hover:border-[#ff6b6b]/40 text-white px-5 py-4 rounded-2xl text-sm font-semibold transition-all hover:bg-[#1a1a24] group"
          data-testid="blocks-continue-btn"
        >
          Continuer
          <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </button>
      )}
    </div>
  );
}


export default function LessonPage() {
  const { courseId, moduleId } = useParams();
  const { t } = useTranslation();
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [activeTab, setActiveTab] = useState('keyPoints');
  const [completed, setCompleted] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [completedLessons, setCompletedLessons] = useState([]);

  useEffect(() => {
    axios.get(`${API}/courses/${courseId}`, { withCredentials: true })
      .then(res => setCourse(res.data))
      .catch(() => navigate('/dashboard'))
      .finally(() => setLoading(false));
    // Load completed lessons for progress dots
    axios.get(`${API}/courses/${courseId}/progress`, { withCredentials: true })
      .then(res => {
        const prog = res.data;
        const done = (prog?.completed_lessons || []).concat(prog?.completed_modules || []);
        setCompletedLessons(done);
      })
      .catch(() => {});
  }, [courseId, navigate]);

  const module = course?.modules?.find(m => m.id === moduleId);
  const lesson = module?.lesson;
  const modules = course?.modules || [];
  const currentIdx = modules.findIndex(m => m.id === moduleId);
  const nextModule = modules[currentIdx + 1];

  const handleCompleteLesson = async () => {
    if (completed || completing) return;
    setCompleting(true);
    try {
      const lessonId = lesson?.id || module?.id || moduleId;
      await axios.post(`${API}/courses/${courseId}/complete-lesson`, { lesson_id: lessonId }, { withCredentials: true });
      setCompleted(true);
      await refreshUser();
    } catch (e) {
      console.error(e);
    } finally {
      setCompleting(false);
    }
  };

  const handleQuizComplete = async (results) => {
    await handleCompleteLesson();
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 pt-6 space-y-4">
          <div className="animate-shimmer h-32 rounded-2xl" />
          <div className="animate-shimmer h-12 rounded-2xl" />
          <div className="animate-shimmer h-64 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  if (!module) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 pt-6 text-center">
          <p className="text-[#e8e8e8]">{t('course.moduleNotFound')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Reader Zone: left module sidebar + main content */}
      <div className="flex h-full" data-testid="reader-zone">

        {/* ── Module Sidebar (desktop: sticky left, mobile: slide overlay) ── */}
        <>
          {/* Mobile overlay */}
          {sidebarOpen && (
            <div
              className="lg:hidden fixed inset-0 bg-black/60 z-40"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          <aside
            className={`
              fixed lg:sticky top-0 left-0 z-50 lg:z-auto h-screen
              w-[260px] bg-[#16161e] border-r border-[#2a2935]
              flex flex-col overflow-hidden
              transition-transform duration-300
              ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}
            data-testid="reader-sidebar"
          >
            <div className="flex items-center justify-between px-4 py-3.5 border-b border-[#2a2935]">
              <button
                onClick={() => navigate(`/course/${courseId}`)}
                className="flex items-center gap-2 text-sm text-[#94a3b8] hover:text-white transition-colors"
                data-testid="reader-back-btn"
              >
                <ChevronLeft size={15} /> {course?.title || 'Cours'}
              </button>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-1.5 text-[#64748b] hover:text-white transition-colors"
              >
                <X size={15} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-3 px-2">
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748b] px-2 mb-2">
                Modules ({modules.length})
              </p>
              {modules.map((mod, idx) => {
                const isActive = mod.id === moduleId;
                const isDone = completedLessons.includes(mod.id) || completedLessons.includes(mod.lesson?.id);
                return (
                  <button
                    key={mod.id}
                    onClick={() => {
                      navigate(`/course/${courseId}/module/${mod.id}`);
                      setSidebarOpen(false);
                    }}
                    data-testid={`reader-module-${idx}`}
                    className={`w-full flex items-start gap-2.5 px-2.5 py-2.5 rounded-xl text-left mb-0.5 transition-all ${
                      isActive
                        ? 'bg-[#ff6b6b]/10 text-[#ff6b6b]'
                        : 'text-[#94a3b8] hover:bg-[#20202a] hover:text-white'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-[10px] font-bold border ${
                      isDone
                        ? 'bg-[#20c997] border-[#20c997] text-[#0f0e17]'
                        : isActive
                        ? 'border-[#ff6b6b] text-[#ff6b6b]'
                        : 'border-[#2a2935] text-[#64748b]'
                    }`}>
                      {isDone ? <CheckCircle size={11} /> : idx + 1}
                    </div>
                    <p className="text-xs font-medium leading-snug line-clamp-2">{mod.title}</p>
                  </button>
                );
              })}
            </div>

            {/* Quick question in sidebar */}
            <div className="px-3 pb-4 pt-2 border-t border-[#2a2935]">
              <button
                onClick={() => { setChatOpen(true); setSidebarOpen(false); }}
                data-testid="sidebar-ask-question-btn"
                className="w-full flex items-center gap-2 bg-[#20c997]/10 border border-[#20c997]/20 text-[#20c997] px-3 py-2.5 rounded-xl text-xs font-medium hover:bg-[#20c997]/15 transition-all"
              >
                <MessageSquare size={13} /> Question sur ce module
              </button>
            </div>
          </aside>
        </>

        {/* ── Main Content ── */}
        <div className="flex-1 min-w-0 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 pt-4 pb-24 space-y-5">
            {/* Mobile header: back + modules toggle */}
            <div className="flex items-center gap-3 lg:hidden animate-fade-up">
              <button
                onClick={() => setSidebarOpen(true)}
                data-testid="open-reader-sidebar-btn"
                className="w-9 h-9 bg-[#16161e] rounded-xl flex items-center justify-center border border-[#2a2935] hover:border-[#3a3945] transition-colors text-[#94a3b8] hover:text-white"
              >
                <Menu size={16} />
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-[#94a3b8] text-xs">{t('course.moduleOrderLabel', { n: module.order })}</p>
                <h1 className="font-heading font-bold text-white text-base leading-tight truncate">{module.title}</h1>
              </div>
            </div>

            {/* Desktop header */}
            <div className="hidden lg:flex items-center gap-3 animate-fade-up">
              <div className="flex-1 min-w-0">
                <p className="text-[#94a3b8] text-xs">{t('course.moduleOrderLabel', { n: module.order })}</p>
                <h1 className="font-heading font-bold text-white text-lg leading-tight truncate">{module.title}</h1>
              </div>
              <button
                onClick={() => setChatOpen(true)}
                data-testid="ask-question-btn"
                className="flex items-center gap-2 bg-[#16161e] border border-[#20c997]/30 text-[#20c997] px-3.5 py-2 rounded-xl text-sm font-medium hover:bg-[#20c997]/10 transition-all shrink-0"
              >
                <MessageSquare size={14} /> Question sur ce module
              </button>
            </div>

        {/* Module banner image */}
        {module.image_url ? (
          <div className="w-full h-40 rounded-2xl overflow-hidden animate-fade-up" style={{ animationDelay: '0.05s' }}>
            <img
              src={module.image_url}
              alt={module.title}
              className="w-full h-full object-cover"
              onError={e => { e.target.style.display = 'none'; e.target.parentElement.style.background = 'linear-gradient(135deg, #1a1a24 0%, #0f0e17 100%)'; }}
            />
          </div>
        ) : (
          <div className="w-full h-40 rounded-2xl animate-fade-up" style={{ animationDelay: '0.05s', background: 'linear-gradient(135deg, rgba(255,107,107,0.08) 0%, rgba(32,201,151,0.06) 100%)', border: '1px solid rgba(255,255,255,0.05)' }} />
        )}

        {/* Lesson title (legacy) */}
        {!module.blocks?.length && lesson && (
          <div className="kurso-card p-5 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <h2 className="font-heading font-semibold text-white text-xl">{lesson.title}</h2>
          </div>
        )}

        {/* ── Blocks Mode (nouveau) ── */}
        {module.blocks && module.blocks.length > 0 ? (
          <div className="animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <BlocksReader
              blocks={module.blocks}
              courseId={courseId}
              moduleId={moduleId}
              lesson={lesson}
              resources={module.resources}
              onComplete={handleQuizComplete}
            />
          </div>
        ) : (
          /* ── Legacy Tabs Mode ── */
          <>
            {/* Tabs */}
            <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                    activeTab === tab.key
                      ? 'bg-[#ff6b6b] text-[#0f0e17]'
                      : 'bg-[#1a1a24] text-[#e8e8e8] hover:text-white border border-white/10'
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  <tab.icon size={14} />
                  {t(`course.tab${tab.key.charAt(0).toUpperCase() + tab.key.slice(1)}`)}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="animate-fade-up" key={activeTab}>
              {lesson ? (
                <>
                  {activeTab === 'keyPoints' && <KeyPoints points={lesson.key_points} />}
                  {activeTab === 'stats' && <Stats stats={lesson.stats} />}
                  {activeTab === 'schema' && <Schema schema={lesson.schema} />}
                  {activeTab === 'resources' && <Resources resources={lesson.resources} />}
                  {activeTab === 'quiz' && (
                    <Quiz
                      quiz={lesson.quiz}
                      lessonId={lesson.id}
                      courseId={courseId}
                      onComplete={handleQuizComplete}
                    />
                  )}
                </>
              ) : (
                <p className="text-[#e8e8e8] text-sm">{t('course.contentGenerating')}</p>
              )}
            </div>
          </>
        )}

        {/* Navigation */}
        <div className="flex gap-3 pt-2">
          {!completed ? (
            <button
              onClick={handleCompleteLesson}
              disabled={completing}
              className="flex-1 btn-secondary py-3.5 text-sm disabled:opacity-50"
              data-testid="complete-lesson-btn"
            >
              {completing ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                  {t('course.saving')}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle size={16} /> {t('course.markDone')}
                </span>
              )}
            </button>
          ) : (
            <div className="flex-1 teal-badge py-3.5 rounded-2xl flex items-center justify-center gap-2" data-testid="lesson-completed">
              <CheckCircle size={16} /> {t('course.doneLabel')}
            </div>
          )}

          {nextModule && completed && (
            <button
              onClick={() => navigate(`/course/${courseId}/module/${nextModule.id}`)}
              className="btn-primary py-3.5 px-5 text-sm"
              data-testid="next-module-btn"
            >
              <ChevronRight size={18} />
            </button>
          )}

          {!nextModule && completed && (
            <button
              onClick={() => navigate(`/course/${courseId}/completion`)}
              className="btn-primary py-3.5 px-5 text-sm"
              data-testid="go-to-completion-btn"
            >
              {t('course.finishBtn')}
            </button>
          )}
        </div>
        </div>{/* end max-w-2xl */}
        </div>{/* end main content */}
      </div>{/* end reader-zone flex */}

      {/* Mobile floating "Question" button */}
      <button
        onClick={() => setChatOpen(true)}
        data-testid="mobile-ask-question-btn"
        className="lg:hidden fixed bottom-20 right-4 z-40 bg-[#20c997] text-[#0f0e17] rounded-2xl px-4 py-3 text-sm font-bold shadow-lg flex items-center gap-2"
      >
        <MessageSquare size={15} /> Question
      </button>

      {/* Chat Panel */}
      <ChatPanel
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        courseId={courseId}
        courseTitle={course?.title}
        moduleTitle={module?.title}
      />
    </Layout>
  );
}
